import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, FileText, MessageSquare, CalendarClock, Clock, CheckCircle2, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatCard, StatusBadge, EmptyState } from "@/components/dashboard/ui";
import UrgentHelp from "@/components/shared/UrgentHelp";

export default function DoctorHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [cap, setCap] = useState(null);

  useEffect(() => {
    api.get("/missions").then(({ data }) => setMissions(data || []));
    api.get("/requests").then(({ data }) => setRequests(data || []));
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/capacity?day=${today}`).then(({ data }) => setCap(data));
  }, []);

  const upcoming = missions.filter((m) => ["Attribuée", "Confirmée", "En cours"].includes(m.status));
  const pending = requests.filter((r) => !["Terminée", "Refusée", "Annulée", "Confirmée"].includes(r.status));
  const capLabel = cap ? (cap.state === "full" ? "Complet" : cap.state === "limited" ? "Limitée" : `${cap.count} dispo.`) : "…";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">Bonjour Dr {user?.last_name || user?.name}</h1>
          <p className="mt-1 text-slate-500">Voici un aperçu de votre activité.</p>
        </div>
        <UrgentHelp />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={CalendarClock} label="Missions à venir" value={upcoming.length} tone="primary" testid="stat-upcoming" />
        <StatCard icon={Clock} label="Demandes en cours" value={pending.length} tone="amber" testid="stat-pending" />
        <StatCard icon={Users} label="Capacité aujourd'hui" value={capLabel} tone="emerald" testid="stat-capacity" />
        <StatCard icon={CheckCircle2} label="Missions confirmées" value={missions.filter((m) => m.status === "Confirmée").length} tone="accent" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <button onClick={() => navigate("/espace/reserver")} className="bg-primary text-white rounded-xl p-5 text-left hover:opacity-95 transition-opacity" data-testid="quick-book">
          <CalendarPlus className="h-6 w-6" /><p className="mt-3 font-semibold">Réserver une disponibilité</p><p className="text-sm text-white/80">Choisissez un créneau et envoyez votre demande.</p>
        </button>
        <button onClick={() => navigate("/espace/demandes")} className="bg-white border border-slate-200 rounded-xl p-5 text-left hover:shadow-md transition-all" data-testid="quick-requests">
          <FileText className="h-6 w-6 text-primary" /><p className="mt-3 font-semibold text-slate-900">Voir mes demandes</p><p className="text-sm text-slate-500">Suivez l'état de vos demandes et missions.</p>
        </button>
        <button onClick={() => navigate("/espace/messages")} className="bg-white border border-slate-200 rounded-xl p-5 text-left hover:shadow-md transition-all" data-testid="quick-messages">
          <MessageSquare className="h-6 w-6 text-primary" /><p className="mt-3 font-semibold text-slate-900">Envoyer un message</p><p className="text-sm text-slate-500">Échangez directement avec notre équipe.</p>
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Prochaines missions</h3>
          {upcoming.length === 0 ? <EmptyState icon={CalendarClock} title="Aucune mission à venir" /> : (
            <div className="space-y-3">{upcoming.slice(0, 5).map((m) => (
              <div key={m.mission_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div><p className="text-sm font-medium text-slate-900">{new Date(m.date).toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" })}</p><p className="text-xs text-slate-500">{m.start} – {m.end} · {(m.services || []).join(", ")}</p></div>
                <StatusBadge status={m.status} />
              </div>))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Demandes récentes</h3>
          {pending.length === 0 ? <EmptyState icon={FileText} title="Aucune demande en cours" action={<Button size="sm" className="bg-primary" onClick={() => navigate("/espace/reserver")}>Faire une demande</Button>} /> : (
            <div className="space-y-3">{pending.slice(0, 5).map((r) => (
              <div key={r.request_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div><p className="text-sm font-medium text-slate-900">{(r.services || []).join(", ") || "Demande"}</p><p className="text-xs text-slate-500">{r.kind === "project" ? "Projet / accompagnement" : (r.dates?.[0]?.date ? new Date(r.dates[0].date).toLocaleDateString("fr-CH") : "Horaire")}</p></div>
                <StatusBadge status={r.status} />
              </div>))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
